import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslate } from "helpers";

import {
  FleetVehiclePictureRow,
} from "services/api/fleet/vehicles/pictures/types";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";

import {
  createVehiclePictureAPI,
  deleteVehiclePictureAPI,
  getFleetVehiclePicturesAPI,
} from "services/api/fleet/vehicles/pictures/api";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";

import { VehiclePictureFormProps } from "./types";

const VehiclePictureForm: React.FC<VehiclePictureFormProps> = (props) => {
  const { formValues, setVehicleInfo } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPictureId, setSelectedPictureId] = useState<string>("");
  const [vehiclePictures, setVehiclePictures] = useState<
    DataWithPagination<FleetVehiclePictureRow>
  >(initialDataWithPagination);

  useEffect(() => {
    if (formValues.id)
      fetchVehiclePictures(vehiclePictures.pagination, formValues.id);
  }, [formValues.id]);

  // Ažuriraj summary kad se slike promijene
  useEffect(() => {
    setVehicleInfo(prev => ({
      ...prev,
      pictureUrls: (vehiclePictures?.data ?? [])
        .filter(pic => pic.url)
        .map(pic => pic.url!)
        .slice(0, 8),
    }));
  }, [vehiclePictures?.data]);

  const fetchVehiclePictures = async (
    pagination: Pagination,
    vehicleId: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehiclePicturesAPI(
        vehicleId,
        pagination,
        ""
      );
      setVehiclePictures(results);
    } catch {
      setVehiclePictures(initialDataWithPagination);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadSave = useCallback(
    async (uploads: FleetDocumentUpload[]) => {
      if (!formValues.id || uploads.length === 0) return;

      setIsLoading(true);
      try {
        await Promise.all(
          uploads.map((upload) =>
            createVehiclePictureAPI(
              formValues.id!,
              upload.id,
              upload.originalFileName,
              "",
              t
            )
          )
        );
        await fetchVehiclePictures(vehiclePictures.pagination, formValues.id);
      } catch {
        // error handled by API interceptors
      } finally {
        setIsLoading(false);
      }
    },
    [formValues.id, vehiclePictures.pagination, t]
  );

  const handlePictureDelete = useCallback((id: string) => {
    setSelectedPictureId(id);
    setOpenDialog(true);
  }, []);

  const handlePictureDeleteDialogConfirm = useCallback(async () => {
    if (!selectedPictureId) return;

    try {
      const deleted = await deleteVehiclePictureAPI(selectedPictureId, t);

      if (deleted && formValues.id) {
        await fetchVehiclePictures(vehiclePictures.pagination, formValues.id);
      }

      setOpenDialog(false);
      setSelectedPictureId("");
    } catch (err) {
      console.error("Greška prilikom brisanja slike:", err);
    }
  }, [selectedPictureId, t, formValues.id, vehiclePictures.pagination]);

  const handlePictureDeleteDialogClose = useCallback(() => {
    setSelectedPictureId("");
    setOpenDialog(false);
  }, []);

  // Convert server pictures to FleetDocumentUpload[] for FileUploadList
  const existingUploads: FleetDocumentUpload[] = useMemo(() => {
    return (vehiclePictures?.data ?? []).map((pic) => ({
      id: pic.id,
      originalFileName: pic.name ?? "",
      fileExtension: pic.url?.split(".").pop() ?? "",
      fileSizeKb: 0,
      contentType: "image/jpeg",
      created: new Date(),
      url: pic.url ?? "",
    }));
  }, [vehiclePictures?.data]);

  if (isLoading) {
    return (
      <div className="relative flex items-center justify-center px-4 h-[calc(100vh-150px)]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  return (
    <>
      <div className="relative flex justify-center px-4 pt-12 h-[calc(100vh-150px)]">
        <div className="w-full max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
            {t("admin.fleet.vehicle.edit.picture.title")}
          </h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            {t("admin.fleet.vehicle.edit.picture.add.description")}
          </p>
          <div className="grid grid-cols-4 gap-2 mb-6 px-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="w-full h-[200px] bg-slate-100 rounded-md overflow-hidden aspect-video flex items-center justify-center">
                <img
                  src={`/media/images/vehicles/vehicle-${i}.png`}
                  alt=""
                  className="w-[180px] h-[180px] object-cover "
                />
              </div>
            ))}
          </div>
          <div className="px-3">
            <FileUploadControl
              uploads={existingUploads}
              onUploadSave={handleUploadSave}
              onUploadDelete={handlePictureDelete}
              buttonText="Dodaj sliku"
              modalTitle="Upload slika"
              emptyText="Dodaj slike"
            />
          </div>
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fleet.vehicle.edit.picture.dialog.delete.title")}
        message={t("admin.fleet.vehicle.edit.picture.dialog.delete.message")}
        confirmText={t("admin.fleet.vehicle.edit.picture.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.vehicle.edit.picture.dialog.delete.canceltext")}
        onConfirm={handlePictureDeleteDialogConfirm}
        onCancel={handlePictureDeleteDialogClose}
      />
    </>
  );
};
export default memo(VehiclePictureForm);
